import React, { Component } from 'react';
import { Link } from 'react-router-dom';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import BandasEventos from '../components/HomeEventoComponent';
import { PanelHome, Fila, SubTitulo, Vacio, TiraRevisar, Boton, COLOR } from '../components/HomePanelComponent';
import { CajaChip } from './HomePage';

import { validateSession } from '../utils/functions';
import { get, patch } from '../utils/apiClient';
import { convertToMoney } from '../utils/formats';
import { conVueltaAlHome } from '../utils/appMode';

// Home de trabajo de cafetería. Mismo esqueleto que el de retail a propósito —se aprende
// una vez y sirve en los dos modos— pero responde otra pregunta.
//
// En retail el trabajo se ACUMULA: hay ventas esperando pago desde hace días y envíos de
// hace una semana, así que el home es una cola. En cafetería el trabajo FLUYE: las
// comandas se abren y se cierran el mismo día. Por eso este home no es una cola, es el
// TURNO EN CURSO, y está anclado al reloj: qué hay sentado ahora, qué está en la cocina,
// qué viene en la próxima hora. Lo que ya pasó se cae solo de la lista.

// La comanda que lleva más de esto esperando ya es un reclamo en la mesa.
const MINUTOS_PARA_URGENTE = 15;

function minutosDesde(fechaIso) {
    if (!fechaIso) return null;
    const cuando = new Date(fechaIso);
    if (isNaN(cuando.getTime())) return null;
    return Math.max(0, Math.round((Date.now() - cuando.getTime()) / 60000));
}

function haceMinutos(fechaIso) {
    const minutos = minutosDesde(fechaIso);
    if (minutos === null) return '';
    if (minutos < 60) return `hace ${minutos} min`;
    const horas = Math.floor(minutos / 60);
    // Pasado el día se cuenta en días: "hace 1675 h" no es una antigüedad, es un número.
    // Una comanda de ayer sin cerrar es un dato distinto a una de hace 40 minutos.
    if (horas >= 24) {
        const dias = Math.floor(horas / 24);
        return dias === 1 ? 'ayer' : `hace ${dias} días`;
    }
    const resto = minutos % 60;
    return resto === 0 ? `hace ${horas} h` : `hace ${horas} h ${String(resto).padStart(2, '0')}`;
}

// El panel muestra unas pocas y el resto queda en "ver comandas". Sin tope, un día con 25
// comandas abiertas empuja los otros tres paneles fuera de la pantalla — y el home deja de
// servir para lo que existe, que es ver el turno entero de un vistazo.
const MAX_FILAS = 6;

class CafeteriaHomePage extends Component {

    constructor() {
        const rolCacheado = Number(localStorage.getItem('role') || 0);
        super();
        this.state = {
            checked: !!localStorage.getItem('token'),
            role: rolCacheado,
            home: null,
            error: null,
            trabajando: {},
        };
        this._desmontado = false;
    }

    async componentDidMount() {
        const role = await validateSession(true);
        this.setState({ checked: true, role });
        this.cargar();
        // El salón cambia solo: una comanda que la cocina marca lista tiene que aparecer
        // acá sin que nadie recargue. Un minuto es el ritmo del turno, no de la cocina —
        // para eso está la pantalla de Comandas, que es la que mira quien cocina.
        this._intervalo = setInterval(this.cargar, 60 * 1000);
    }

    componentWillUnmount() {
        this._desmontado = true;
        clearInterval(this._intervalo);
    }

    cargar = () => get('home/cafeteria')
        .then((data) => {
            if (this._desmontado) return;
            this.setState({ home: data, error: null });
        })
        .catch((error) => {
            if (this._desmontado) return;
            this.setState({ error: error.message || 'No se pudo cargar' });
        });

    resolver = (clave, promesa) => {
        this.setState({ trabajando: { ...this.state.trabajando, [clave]: true } });
        promesa
            .then(() => this.cargar())
            .catch((error) => alert(error.message || 'No se pudo completar la operación'))
            .then(() => {
                if (this._desmontado) return;
                const trabajando = { ...this.state.trabajando };
                delete trabajando[clave];
                this.setState({ trabajando });
            });
    }

    marcarComanda = (orden, estado) => this.resolver(
        `comanda-${orden.id}`,
        patch(`cafeteria/kitchen-order/${orden.id}/status`, { status: estado })
    );

    // ── Paneles ──────────────────────────────────────────────────────────────

    panelSalon() {
        const bloque = this.state.home ? this.state.home.salon : null;
        if (!bloque) {
            return (
                <PanelHome icono="weekend" titulo="En el salón ahora" verTodo="/reservations" verTodoTexto="Ver salón" cargando={!this.state.home}>
                    <Vacio texto="No se pudo cargar el salón." />
                </PanelHome>
            );
        }

        const sentadas = bloque.sentadas || [];

        return (
            <PanelHome
                icono="weekend" titulo="En el salón ahora"
                contador={sentadas.length} tono="gris"
                verTodo="/reservations" verTodoTexto="Ver salón"
            >
                {sentadas.length === 0 ? <Vacio texto="No hay mesas sentadas." /> : null}
                {sentadas.map((mesa, indice) => (
                    <Fila
                        key={mesa.reservationId}
                        primero={indice === 0}
                        titulo={`${mesa.mesa || 'Sin mesa'} · ${mesa.personas} ${mesa.personas === 1 ? 'persona' : 'personas'}${mesa.jornada ? ` · ${mesa.jornada}` : mesa.juego ? ` · ${mesa.juego}` : ''}`}
                        detalle={mesa.consumo > 0 ? `$${convertToMoney(mesa.consumo)} en la cuenta` : 'Sin consumo'}
                        meta={haceMinutos(mesa.desde)}
                        accion="Cobrar"
                        href="/reservations"
                    />
                ))}
            </PanelHome>
        );
    }

    panelCocina() {
        const bloque = this.state.home ? this.state.home.cocina : null;
        if (!bloque) {
            return (
                <PanelHome icono="restaurant_menu" titulo="Cocina" verTodo="/cafeteriaOrders" verTodoTexto="Ver comandas" cargando={!this.state.home}>
                    <Vacio texto="No se pudo cargar la cocina." />
                </PanelHome>
            );
        }

        const listas = bloque.listas || [];
        const enPreparacion = bloque.enPreparacion || [];
        const total = listas.length + enPreparacion.length;
        // Lo listo NUNCA se recorta: es lo que se está enfriando arriba del mostrador.
        // El tope se lo come lo que está en preparación, que es donde se acumula.
        const preparacionVisible = enPreparacion.slice(0, Math.max(0, MAX_FILAS - listas.length));
        const ocultas = enPreparacion.length - preparacionVisible.length;

        return (
            <PanelHome
                icono="restaurant_menu" titulo="Cocina"
                contador={total} tono={enPreparacion.length > 0 ? 'alerta' : 'gris'}
                verTodo="/cafeteriaOrders" verTodoTexto="Ver comandas"
            >
                {total === 0 ? <Vacio texto="No hay comandas abiertas." /> : null}

                {/* Lo que está listo va ARRIBA: es lo que se enfría mientras nadie lo lleva. */}
                {listas.length > 0 ? <SubTitulo texto={`LISTA PARA LLEVAR A LA MESA · ${listas.length}`} tono="ok" /> : null}
                {listas.map((orden, indice) => this.filaComanda(orden, indice === 0, 'Entregada', 'delivered', false))}

                {enPreparacion.length > 0 ? <SubTitulo texto={`EN PREPARACIÓN · ${enPreparacion.length}`} /> : null}
                {preparacionVisible.map((orden, indice) => this.filaComanda(orden, indice === 0, 'Lista', 'ready', true))}

                {ocultas > 0 ? (
                    <div style={{ padding: '7px 16px 9px 16px', fontSize: 12, color: COLOR.apagado }}>
                        + {ocultas} más
                    </div>
                ) : null}
            </PanelHome>
        );
    }

    filaComanda(orden, primero, accion, estado, miraElReloj) {
        const minutos = minutosDesde(orden.creada);
        const trabajando = this.state.trabajando[`comanda-${orden.id}`];
        const detalle = (orden.items || [])
            .map((item) => (item.cantidad > 1 ? `${item.cantidad}× ${item.nombre}` : item.nombre))
            .join(' · ');

        return (
            <Fila
                key={orden.id}
                primero={primero}
                titulo={orden.destino || orden.cliente || `Comanda #${orden.id}`}
                detalle={detalle}
                meta={haceMinutos(orden.creada)}
                urgente={miraElReloj && minutos !== null && minutos >= MINUTOS_PARA_URGENTE}
                accion={trabajando ? '…' : accion}
                onAccion={trabajando ? undefined : () => this.marcarComanda(orden, estado)}
            />
        );
    }

    panelAgenda() {
        const agenda = this.state.home ? this.state.home.agenda : null;
        if (!agenda) {
            return (
                <PanelHome icono="event" titulo="Lo que viene hoy" verTodo="/reservations" cargando={!this.state.home}>
                    <Vacio texto="No se pudo cargar la agenda." />
                </PanelHome>
            );
        }

        const ahora = new Date();
        const hora = `${String(ahora.getHours()).padStart(2, '0')}:${String(ahora.getMinutes()).padStart(2, '0')}`;

        return (
            <PanelHome
                icono="event" titulo="Lo que viene hoy"
                verTodo="/reservations"
                verTodoTexto={`son las ${hora}`}
            >
                {agenda.length === 0 ? <Vacio texto="No queda nada agendado para hoy." /> : null}
                {agenda.map((item, indice) => item.tipo === 'evento' ? (
                    // Un evento de hoy que no ocupa mesas del salón: igual es algo que viene.
                    <Fila
                        key={item.reservationId}
                        primero={indice === 0}
                        titulo={`${item.hora} · ${item.nombre}`}
                        detalle={`${item.anotados} ${item.anotados === 1 ? 'anotado' : 'anotados'} · evento sin mesas del salón`}
                        accion="Ver anotados"
                        href={`/eventAdmissions?id=${item.eventId}`}
                    />
                ) : item.tipo === 'jornada' ? (
                    // Una jornada del club llega agrupada: una fila con todas sus mesas y los
                    // anotados del evento, en vez de una fila por mesa con "0 personas".
                    <Fila
                        key={item.reservationId}
                        primero={indice === 0}
                        titulo={`${item.hora} · ${item.nombre}`}
                        detalle={[
                            `${item.anotados} ${item.anotados === 1 ? 'anotado' : 'anotados'}`,
                            item.mesas.length ? `${item.mesas.length} ${item.mesas.length === 1 ? 'mesa' : 'mesas'} (${item.mesas.join(', ')})` : 'sin mesas',
                        ].join(' · ')}
                        accion="Ver anotados"
                        href={item.eventId ? `/eventAdmissions?id=${item.eventId}` : '/events'}
                    />
                ) : (
                    <Fila
                        key={item.reservationId}
                        primero={indice === 0}
                        titulo={`${item.hora} · ${item.personas} ${item.personas === 1 ? 'persona' : 'personas'}${item.nombre ? ` · ${item.nombre}` : ''}`}
                        detalle={[
                            item.juego,
                            item.mesa ? `mesa ${item.mesa}` : 'mesa sin asignar',
                            item.sinConfirmar ? 'sin confirmar' : null,
                        ].filter(Boolean).join(' · ')}
                        urgente={item.sinConfirmar}
                        accion={item.mesa ? 'Ver' : 'Asignar mesa'}
                        href="/reservations"
                    />
                ))}
            </PanelHome>
        );
    }

    panelSinCobrar() {
        const bloque = this.state.home ? this.state.home.sinCobrar : null;
        if (!bloque) {
            return (
                <PanelHome icono="request_quote" titulo="Sin cobrar" cargando={!this.state.home}>
                    <Vacio texto="No se pudo cargar lo que falta cobrar." />
                </PanelHome>
            );
        }

        const total = bloque.entradasSinCobrar + bloque.holds + bloque.devolucionesPendientes + bloque.reservasSinConfirmar;
        const filas = [];

        // Los holds primero: son los únicos con reloj. Si no se resuelven, vencen y liberan
        // el lugar solos — y alguien se queda sin entrada sin que nadie lo haya decidido.
        if (bloque.holds > 0) {
            filas.push({
                clave: 'holds',
                titulo: `${bloque.holds} ${bloque.holds === 1 ? 'reserva de entrada por confirmar' : 'reservas de entrada por confirmar'}`,
                detalle: 'Si no se confirman, vencen y liberan el lugar',
                urgente: true, destacada: true, accion: 'Resolver', href: '/events',
            });
        }
        if (bloque.entradasSinCobrar > 0) {
            filas.push({
                clave: 'unpaid',
                titulo: `${bloque.entradasSinCobrar} ${bloque.entradasSinCobrar === 1 ? 'entrada vendida sin cobrar' : 'entradas vendidas sin cobrar'}`,
                detalle: 'Vendidas en el mostrador, sin pasar por caja',
                accion: 'Cobrar', href: '/events',
            });
        }
        if (bloque.devolucionesPendientes > 0) {
            filas.push({
                clave: 'refund',
                titulo: `${bloque.devolucionesPendientes} ${bloque.devolucionesPendientes === 1 ? 'devolución pendiente' : 'devoluciones pendientes'}`,
                detalle: 'Entrada cancelada, plata sin devolver',
                accion: 'Devolver', href: '/events',
            });
        }
        if (bloque.reservasSinConfirmar > 0) {
            filas.push({
                clave: 'reservas',
                titulo: `${bloque.reservasSinConfirmar} ${bloque.reservasSinConfirmar === 1 ? 'reserva sin confirmar' : 'reservas sin confirmar'}`,
                detalle: 'Esperan la seña o la transferencia',
                accion: 'Revisar', href: '/reservations',
            });
        }

        return (
            <PanelHome
                icono="request_quote" titulo="Sin cobrar"
                contador={total} tono={bloque.holds > 0 ? 'alerta' : total > 0 ? 'aviso' : 'gris'}
            >
                {filas.length === 0 ? <Vacio texto="No queda nada por cobrar." /> : null}
                {filas.map((fila, indice) => (
                    <Fila key={fila.clave} primero={indice === 0} {...fila} />
                ))}
            </PanelHome>
        );
    }

    tiraRevisar() {
        const bloque = this.state.home ? this.state.home.revisar : null;
        if (!bloque) {
            return null;
        }
        return (
            <TiraRevisar items={[
                {
                    titulo: bloque.insumosBajoMinimo > 0
                        ? `${bloque.insumosBajoMinimo} ${bloque.insumosBajoMinimo === 1 ? 'insumo bajo el mínimo' : 'insumos bajo el mínimo'}`
                        : 'Insumos al día',
                    alerta: bloque.insumosBajoMinimo > 0, ruta: '/cafeteriaInsumos', accion: 'Reponer',
                },
                {
                    titulo: bloque.juegosEnMesas > 0
                        ? `${bloque.juegosEnMesas} ${bloque.juegosEnMesas === 1 ? 'juego en mesas' : 'juegos en mesas'} sin volver`
                        : 'Sin juegos fuera de la estantería',
                    alerta: bloque.juegosEnMesas > 0, ruta: '/ludoteca', accion: 'Ver', tono: 'aviso',
                },
            ]} />
        );
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }
        const nombre = localStorage.getItem('firstName') || '';
        const home = this.state.home;
        const caja = home ? home.caja : null;
        const cocina = home ? home.cocina : null;
        const salon = home ? home.salon : null;
        const agenda = home ? home.agenda : null;

        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4" style={{ paddingBottom: '60px' }}>
                            <HeaderComponent title="Inicio" subtitle={nombre ? `Hola, ${nombre}` : 'El turno'} />

                            {this.state.error ? (
                                <div className="alert alert-warning" style={{ fontSize: 13 }}>
                                    No se pudo cargar el home: {this.state.error}
                                </div>
                            ) : null}

                            {/* La barra del turno: la acción más usada, los cuatro números del
                                momento, y la caja — que es del mismo cajón que la tienda. */}
                            <div className="card card-small mb-4">
                                <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                                    <Link to={conVueltaAlHome('/cafeteria')} className="btn btn-primary" style={{ flexShrink: 0 }}>Nueva comanda</Link>
                                    <Numero valor={salon ? salon.sentadas.length : null} etiqueta="mesas sentadas" />
                                    <Numero valor={cocina ? cocina.enPreparacion.length : null} etiqueta="en cocina" tono={cocina && cocina.enPreparacion.length > 0 ? 'alerta' : null} />
                                    <Numero valor={cocina ? cocina.listas.length : null} etiqueta="listas para llevar" tono={cocina && cocina.listas.length > 0 ? 'ok' : null} />
                                    <Numero valor={agenda ? agenda.length : null} etiqueta="por llegar" />
                                    <span style={{ flexGrow: 1 }} />
                                    <CajaChip caja={caja} />
                                </div>
                            </div>

                            {/* El evento de hoy, con la puerta: quién falta entrar y marcarlo acá. */}
                            <BandasEventos eventos={home ? home.eventosHoy : null} onCambio={this.cargar} />

                            <div className="row">
                                <div className="col-12 col-xl-6 mb-4">{this.panelSalon()}</div>
                                <div className="col-12 col-xl-6 mb-4">{this.panelCocina()}</div>
                                <div className="col-12 col-xl-6 mb-4">{this.panelAgenda()}</div>
                                <div className="col-12 col-xl-6 mb-4">{this.panelSinCobrar()}</div>
                            </div>

                            {this.tiraRevisar()}
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

function Numero({ valor, etiqueta, tono }) {
    const color = tono === 'alerta' ? COLOR.alerta : tono === 'ok' ? COLOR.ok : COLOR.texto;
    return (
        <div style={{ flexShrink: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1, color }}>
                {valor === null || valor === undefined ? '…' : valor}
            </div>
            <div style={{ fontSize: 11, color: COLOR.apagado, marginTop: 3 }}>{etiqueta}</div>
        </div>
    );
}

export default CafeteriaHomePage;
